import { el, clear } from "../components/dom.js";
import { progressField, priceNoteField, loanField, startAgainButton } from "../components/tracking-fields.js";
import { reviewField, tagsField } from "../components/review-tags.js";
import { tagSuggestions } from "../utils/filters.js";
import { inTheNews } from "../components/in-the-news.js";
import { offerUndoDelete } from "../components/undo.js";
import { historySection } from "../components/history-section.js";
import {
	ENGAGEMENT_STATUSES,
	BOOK_OWNERSHIP_STATUSES,
	OWNERSHIP_FORMATS,
	TIMES_FINISHED,
	AWARDS,
	awardLabel,
	statusLabel,
} from "../components/status-badge.js";
import {
	getComicById,
	updateComic,
	deleteComic,
	setComicRating,
	setComicFormats,
	setComicFormat,
	setComicTimesFinished,
	setComicAwards,
	setComicNote,
	setComicCollects,
	NOTE_MAX_LENGTH,
} from "../db/comics.js";
import {
	COMIC_FORMATS,
	comicFormatLabel,
	creditLines,
	isCollection,
	seriesLabel,
	roleGroupOf,
} from "../utils/comics.js";
import { getGroupsContainingItem } from "../db/groups.js";
import { getSubscription, subscribe, unsubscribe } from "../db/comic-subscriptions.js";
import { groupsSection } from "../components/groups-section.js";
import { showCollectsSheet } from "../components/collects-sheet.js";
import { showToast } from "../components/toast.js";
import { formatDate, formatReleaseDate } from "../utils/dates.js";
import { navigate, navigateBack, rerenderRoute } from "../main.js";
import { openItemMenu } from "../components/item-menu.js";
import { overriddenFields } from "../utils/overrides.js";
import { statusSticker } from "../components/magazine/status.js";
import { MAG, magGlass, magButton } from "../components/magazine/kit.js";
import {
	magDetailHero,
	magSummary,
	magField,
	magSelectRow,
	magChips,
	magSingleChips,
	magRating,
	magNote,
	magDeleteFooter,
	magDateLine,
} from "../components/magazine/detail.js";

// The comic detail screen — the movie detail's layout with comic facts (series,
// credits, ISBN), an "Edition" picker (issue / TPB / HC / omnibus), and for
// collected editions the "Collects…" editor: the issues this trade contains,
// entered by the user (Metron rarely knows — design_documents/Comics/metron-spike.md)
// plus Metron's own reprint list when it has one.

const SOURCE_NAME = { metron: "Metron", openlibrary: "Open Library" };

export async function renderComicDetail(root, id) {
	const comic = await getComicById(id);
	if (!comic) {
		root.append(
			el(
				"div.view",
				{},
				el("p", {}, "Comic not found."),
				el(
					"button.btn",
					{ type: "button", onclick: () => navigateBack("/comics") },
					"Back",
				),
			),
		);
		return;
	}

	const view = el("div.view", {});
	root.append(view);
	mount(view, comic);
}

async function mount(view, comic) {
	// Re-mounting rebuilds the whole view. Capture scroll up-front, do the async
	// work while the old view is still on screen, then swap synchronously.
	const scrollY = window.scrollY;

	const groups = await getGroupsContainingItem(comic);
	const subscription = comic.series?.id ? await getSubscription(comic.series.id) : null;
	const remount = async (next) => mount(view, next ?? (await getComicById(comic.id)));

	// ---- feature hero ----
	const kickerBits = ["Comic", comicFormatLabel(comic.format)];
	if (comic.release_year) kickerBits.push(String(comic.release_year));
	if (comic.publisher) kickerBits.push(comic.publisher);
	if (overriddenFields(comic).length > 0) kickerBits.push("Edited");

	const figures = [{ value: comic.rating != null ? comic.rating : "—", label: "Rating" }];
	if (isCollection(comic)) {
		const n = collectedCount(comic);
		if (n > 0) figures.push({ value: String(n), label: "Issues" });
	} else if (comic.number) {
		figures.push({ value: `#${comic.number}`, label: "Issue" });
	}

	const hero = magDetailHero(comic, {
		kicker: kickerBits.join(" · "),
		title: comic.title,
		sticker: statusSticker(comic.engagement_status, "comic"),
		figures,
		tag: (comic.awards ?? [])[0] ? awardLabel(comic.awards[0]) : null,
		onBack: () => navigateBack("/comics"),
		// "⋯": edit details / reset (re-link isn't offered for comics yet).
		onMenu: () =>
			openItemMenu({
				mediaType: "comic",
				record: comic,
				onChanged: (updated, { relinked } = {}) =>
					relinked ? rerenderRoute() : mount(view, updated),
			}),
	});

	// ---- fields ----
	const ownsIt =
		comic.ownership_status === "have_it" || comic.ownership_status === "had_it";

	const engagementField = magField(
		"Read it?",
		magSelectRow(ENGAGEMENT_STATUSES, comic.engagement_status, async (value) => {
			mount(view, await updateComic(comic.id, { engagement_status: value }));
		}, (v) => statusLabel(v, "comic")),
		{ index: 0 },
	);

	const ownershipField = magField(
		"Got it?",
		magSelectRow(BOOK_OWNERSHIP_STATUSES, comic.ownership_status ?? "have_it", async (value) => {
			mount(view, await updateComic(comic.id, { ownership_status: value }));
		}, (v) => statusLabel(v, "comic")),
		{ index: 1 },
	);

	const selectedFormats = new Set(comic.formats ?? []);
	const formatField = ownsIt && magField(
		"Format",
		magChips(OWNERSHIP_FORMATS, selectedFormats, async (next) => {
			mount(view, await setComicFormats(comic.id, Array.from(next)));
		}, (v) => statusLabel(v)),
		{ index: 2 },
	);

	const editionField = magField(
		"Edition",
		magSingleChips(
			COMIC_FORMATS.map((f) => ({ value: f, label: comicFormatLabel(f) })),
			comic.format ?? null,
			async (value) => {
				if (!value) return; // an edition type is always set — ignore the un-tap
				mount(view, await setComicFormat(comic.id, value));
			},
		),
		{ index: 3 },
	);

	const collectsField = isCollection(comic) && magField(
		"Collects",
		collectsPanel(comic, remount),
		{ index: 4 },
	);

	const ratingField = magField(
		"Rating",
		magRating(comic.rating ?? null, async (value) => {
			mount(view, await setComicRating(comic.id, value));
		}),
		{ index: 3 },
	);

	const selectedAwards = new Set(comic.awards ?? []);
	const awardsField = magField(
		"Awards",
		magChips(AWARDS.map((a) => a.value), selectedAwards, async (next) => {
			mount(view, await setComicAwards(comic.id, Array.from(next)));
		}, awardLabel),
		{ index: 4 },
	);

	const timesFinishedField = comic.engagement_status === "done" && magField(
		"Times read",
		magSingleChips(TIMES_FINISHED, comic.times_finished ?? null, async (value) => {
			mount(view, await setComicTimesFinished(comic.id, value));
		}),
		{ index: 2 },
	);

	const noteField = magField(
		"Notes",
		magNote(comic.note ?? "", {
			maxLength: NOTE_MAX_LENGTH,
			placeholder: 'A quick note — e.g. "the Bachalo art is wild"',
			onSave: (v) => setComicNote(comic.id, v),
		}),
		{ index: 5 },
	);

	const groupsField = magField(
		"Groups",
		await groupsSection(comic, groups, () => remount()),
		{ index: 1 },
	);

	const dates = magDateLine([
		["First read", formatDate(comic.date_first_completed)],
		["Last read", formatDate(comic.last_engaged_date)],
		["Added", formatDate(comic.created_at)],
	]);

	const footer = magDeleteFooter("Delete from library", async () => {
		if (!confirm(`Delete "${comic.title}" from your list?`)) return;
		await deleteComic(comic.id);
		navigate("/comics");
		offerUndoDelete("comic", comic, { reopenRoute: `/comics/${comic.id}` });
	});

	clear(view);
	view.append(
		hero,
		magSummary(comic.summary) || "",
		inTheNews(comic),
		factsPanel(comic) || "",
		creatorLinks(comic) || "",
		seriesPanel(comic, subscription, () => remount()) || "",
		engagementField,
		ownershipField,
		formatField || "",
		editionField,
		collectsField || "",
		ratingField,
		awardsField,
		timesFinishedField || "",
		noteField,
		tagsField("comic", comic, { suggestions: tagSuggestions("comic") }),
		reviewField("comic", comic),
		progressField("comic", comic),
		priceNoteField("comic", comic),
		loanField("comic", comic),
		startAgainButton("comic", comic),
		groupsField,
		dates,
		historySection(comic),
		footer,
	);

	window.scrollTo(0, scrollY);
}

// How many issues the "Collects…" entries add up to (Metron's list when the
// user hasn't entered any).
function collectedCount(comic) {
	let n = 0;
	for (const entry of comic.collects ?? []) {
		for (const part of String(entry.ranges ?? "").split(",")) {
			const [a, b] = part.split("–").map((x) => Number(x.trim()));
			if (Number.isFinite(a)) n += Number.isFinite(b) ? b - a + 1 : 1;
		}
	}
	return n || (comic.reprints ?? []).length;
}

// Read-only catalog facts: series, credits, publication details.
function factsPanel(comic) {
	const lines = creditLines(comic.creators);
	const rows = [
		["Series", comic.series ? `${seriesLabel(comic.series)}${comic.number && !isCollection(comic) ? ` #${comic.number}` : ""}` : ""],
		["Story", (comic.story_titles ?? []).filter((t) => t && t !== "[Untitled]").slice(0, 3).join("; ")],
		["Writing", lines.writer.join(", ")],
		["Art", lines.art.join(", ")],
		["Cover", lines.cover.join(", ")],
		["Cover date", comic.cover_date ? formatReleaseDate(comic.cover_date) : ""],
		["Publisher", comic.publisher ?? ""],
		["Pages", comic.page_count ? String(comic.page_count) : ""],
		["ISBN", comic.isbn ?? ""],
		["Source", SOURCE_NAME[comic.source] ?? ""],
	].filter(([, v]) => v);
	if (rows.length === 0) return null;

	const list = rows.map(([label, value]) =>
		el(
			"div",
			{ style: { display: "flex", gap: "12px", alignItems: "baseline" } },
			el("span", {
				style: {
					flex: "none", width: "86px",
					fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "8.5px",
					letterSpacing: ".1em", textTransform: "uppercase", color: MAG.dim,
				},
			}, label),
			el("span", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "500", fontSize: "12.5px",
					lineHeight: "1.4", color: "#cfcfd6", minWidth: "0",
				},
			}, value),
		),
	);
	return el(
		"div",
		{ style: { padding: "12px 16px 0" } },
		magGlass(list, {
			style: { display: "flex", flexDirection: "column", gap: "7px", padding: "12px 14px" },
		}),
	);
}

// The issue's Metron series: open its page (every issue, + Add), and 🔔 to
// subscribe for release notices (design_documents/Comics/series-subscriptions.md).
function seriesPanel(comic, subscription, onChanged) {
	const sid = comic.series?.id;
	if (comic.source !== "metron" || !sid) return null;
	const label = seriesLabel(comic.series);
	const bell = el("button", {
		type: "button",
		onclick: async (e) => {
			e.stopPropagation();
			bell.disabled = true;
			if (subscription) {
				await unsubscribe(sid);
				showToast(`Unsubscribed from ${label}`);
			} else {
				await subscribe(sid, { label });
				showToast(`Subscribed — you'll get a notice when something new in ${label} comes out.`);
			}
			onChanged();
		},
		style: {
			flex: "none", fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "10px",
			letterSpacing: ".05em", textTransform: "uppercase", borderRadius: "999px", padding: "8px 12px",
			cursor: "pointer", color: subscription ? "#0b0b0f" : MAG.ink,
			background: subscription ? MAG.lime : "rgba(255,255,255,.07)",
			border: subscription ? "0" : `1px solid ${MAG.line}`,
		},
	}, subscription ? "🔔 Subscribed" : "🔔 Subscribe");
	return el(
		"div",
		{ style: { padding: "10px 16px 0" } },
		magGlass(
			[
				el("div", { style: { flex: "1", minWidth: "0" } },
					el("div", {
						style: {
							fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "8.5px",
							letterSpacing: ".1em", textTransform: "uppercase", color: MAG.dim,
						},
					}, "Series"),
					el("div", { style: { fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "13px", color: MAG.ink } }, `${label} ›`)),
				bell,
			],
			{
				role: "button",
				onClick: () => navigate(`/comics/series/${sid}`),
				style: { display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px" },
			},
		),
	);
}

// Writers and artists credited on a Metron issue, as chips that open their
// creator page (views/comic-creator.js). Cover-only and other roles (editors,
// letterers…) are left out. Open Library comics carry no Metron ids → none.
function creatorLinks(comic) {
	const people = (comic.creators ?? []).filter(
		(c) => c.id != null && (c.roles ?? []).some((r) => roleGroupOf(r) === "writer" || roleGroupOf(r) === "art"),
	);
	if (people.length === 0) return null;
	return el(
		"div",
		{ style: { padding: "10px 16px 0" } },
		el("div", {
			style: {
				fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "8.5px",
				letterSpacing: ".1em", textTransform: "uppercase", color: MAG.dim, marginBottom: "6px",
			},
		}, "See their runs"),
		el(
			"div",
			{ style: { display: "flex", gap: "7px", flexWrap: "wrap" } },
			...people.map((c) =>
				el("button", {
					type: "button",
					onclick: () => navigate(`/comics/creator/${c.id}`),
					style: {
						fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "11px",
						borderRadius: "999px", padding: "7px 12px", cursor: "pointer",
						color: MAG.ink, background: "rgba(31,191,95,.16)", border: "1px solid rgba(31,191,95,.5)",
					},
				}, `${c.name} ›`),
			),
		),
	);
}

const LINE_STYLE = {
	fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "12.5px",
	lineHeight: "1.4", color: "#cfcfd6",
};

// The "Collects…" editor: the user's entries (tap to edit, × to remove), an add
// button, and — when Metron lists the contents — Metron's reprint list.
function collectsPanel(comic, remount) {
	const entries = comic.collects ?? [];
	const wrap = el("div", { style: { display: "flex", flexDirection: "column", gap: "7px" } });

	async function save(list) {
		try {
			await remount(await setComicCollects(comic.id, list));
		} catch (err) {
			showToast(err?.message ?? "Could not save.");
		}
	}

	entries.forEach((entry, i) => {
		wrap.append(
			el(
				"div",
				{
					style: {
						display: "flex", alignItems: "center", gap: "8px", borderRadius: "10px",
						padding: "8px 10px", background: "rgba(255,255,255,.06)", border: `1px solid ${MAG.line}`,
					},
				},
				el(
					"button",
					{
						type: "button",
						title: "Edit",
						onclick: async () => {
							const next = await showCollectsSheet({ initial: entry });
							if (!next) return;
							await save(entries.map((e, j) => (j === i ? next : e)));
						},
						style: {
							...LINE_STYLE, flex: "1", minWidth: "0", textAlign: "left", background: "none",
							border: "0", padding: "0", cursor: "pointer",
						},
					},
					`${entry.series_label} #${entry.ranges}`,
				),
				el(
					"button",
					{
						type: "button",
						title: "Remove",
						"aria-label": `Remove ${entry.series_label}`,
						onclick: async () => {
							if (!confirm(`Remove ${entry.series_label} #${entry.ranges}?`)) return;
							await save(entries.filter((_, j) => j !== i));
						},
						style: {
							flex: "none", background: "none", border: "0", color: MAG.dim,
							fontSize: "16px", cursor: "pointer", padding: "0 4px",
						},
					},
					"×",
				),
			),
		);
	});

	if (entries.length === 0) {
		wrap.append(
			el("div", { style: { ...LINE_STYLE, color: MAG.dim, fontWeight: "500" } },
				"Which issues are in this book? Add them so the creator view can tell what you own."),
		);
	}

	wrap.append(
		magButton("+ Add issues", {
			variant: "ghost",
			onClick: async () => {
				// Trade titles usually lead with the series ("X-Men: Supernovas").
				const suggestion = String(comic.title ?? "").split(/[:(]/)[0].trim();
				const next = await showCollectsSheet({ suggestion });
				if (!next) return;
				await save([...entries, next]);
			},
			style: { alignSelf: "flex-start" },
		}),
	);

	const reprints = comic.reprints ?? [];
	if (reprints.length > 0) {
		wrap.append(
			el("div", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "8.5px",
					letterSpacing: ".1em", textTransform: "uppercase", color: MAG.dim, marginTop: "6px",
				},
			}, "Metron lists"),
			el("div", { style: { ...LINE_STYLE, fontWeight: "500" } }, reprints.map((r) => r.label).join(" · ")),
		);
	}
	return wrap;
}
